import React, { useState, useEffect } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { Badge, Btn, Modal, Avatar } from '../../components/ui';
import { Icon } from '../../icons';
import { formatDateByPreference } from '../../utils/formatters';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import ServiceProviderDocuments from './ServiceProviderDocuments';
import ServiceProviderHistory from './ServiceProviderHistory';

const translations = { en, hi, gu };
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ViewVisitorServiceProviderModal = ({
    provider,
    onClose,
    onEdit,
    onStatusChange
}) => {
    const { language = 'en' } = useSettings();
    const t = (key, params) => {
        let str = translations[language]?.service_providers?.[key] || translations.en?.service_providers?.[key] || key;
        if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
        return str;
    };

    const [activeTab, setActiveTab] = useState('overview');
    const [entries, setEntries] = useState(provider?.entries || []);
    const [loadingEntries, setLoadingEntries] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (provider?.id) {
            fetchRecentLogs();
        }
    }, [provider?.id]);

    const fetchRecentLogs = async () => {
        try {
            setLoadingEntries(true);
            const res = await axios.get('/api/v1/gate-visitor-service-providers/logs', {
                params: { provider_id: provider.id, per_page: 10 }
            });
            if (res.data?.data) {
                setEntries(res.data.data);
            } else if (Array.isArray(res.data)) {
                setEntries(res.data);
            }
        } catch (e) {
            console.error('Error fetching provider logs:', e);
        } finally {
            setLoadingEntries(false);
        }
    };

    if (!provider) return null;

    const isBlocked = !provider.is_active && (provider.notes || '').includes('[BLOCKED]');
    const isInactive = !provider.is_active && !isBlocked;

    const handleAction = async (action) => {
        try {
            setActionLoading(true);
            await axios.patch(`/api/v1/visitor-service-providers/${provider.id}/status`, { action });
            toast.success(`Provider ${action}d successfully`);
            if (onStatusChange) onStatusChange();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || `Failed to ${action} provider`);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Modal title={t("title_provider_details", "Service Provider Details")} onClose={onClose} size="lg">
            <div className="border-b dark:border-slate-800 mb-4 flex gap-6 px-1">
                <button
                    className={`pb-2 text-sm font-semibold transition-colors ${activeTab === 'overview' ? 'border-b-2 border-primary text-primary dark:text-indigo-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button
                    className={`pb-2 text-sm font-semibold transition-colors ${activeTab === 'documents' ? 'border-b-2 border-primary text-primary dark:text-indigo-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setActiveTab('documents')}
                >
                    Documents
                </button>
                <button 
                    className={`pb-2 font-medium transition-colors ${activeTab === 'history' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setActiveTab('history')}
                >
                    History
                </button>
            </div>

            {activeTab === 'overview' ? (
                <div className="space-y-5 text-slate-900 dark:text-slate-100 max-h-[75vh] overflow-y-auto px-1">
                    {/* Header Profile Section */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b dark:border-slate-800 pb-4">
                        <div className="flex items-center gap-3.5">
                            <Avatar
                                src={provider.photo_url}
                                name={provider.name}
                                size={56}
                                className="rounded-full border-2 border-primary shadow-md flex-shrink-0"
                            />
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-bold text-ink dark:text-white">{provider.name}</h3>
                                    <span className="text-xs font-mono font-bold text-primary dark:text-indigo-400 bg-primary/10 px-2 py-0.5 rounded">
                                        #{provider.id}
                                    </span>
                                </div>
                                <p className="text-xs text-muted dark:text-slate-400 capitalize mt-0.5">
                                    {t("type_" + (provider.service_type || '').toLowerCase().replace(/ /g, "_"))}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            {onEdit && (
                                <Btn sm variant="outline" icon="edit" onClick={() => { onClose(); onEdit(provider); }}>
                                    {t("btn_edit", "Edit Provider")}
                                </Btn>
                            )}
                            {provider.is_active ? (
                                <>
                                    <Btn sm variant="outline" disabled={actionLoading} onClick={() => handleAction('deactivate')} className="!text-amber-600 !border-amber-300 hover:!bg-amber-50">
                                        Deactivate
                                    </Btn>
                                    <Btn sm variant="outline" disabled={actionLoading} onClick={() => handleAction('block')} className="!text-red-600 !border-red-300 hover:!bg-red-50">
                                        Block Access
                                    </Btn>
                                </>
                            ) : (
                                <Btn sm variant="primary" disabled={actionLoading} onClick={() => handleAction('activate')}>
                                    Activate Access
                                </Btn>
                            )}
                        </div>
                    </div>

                    {/* Status & Security Verification Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
                        <div>
                            <div className="text-muted dark:text-slate-400 mb-1">{t("col_status", "Account Status")}</div>
                            {isBlocked ? (
                                <Badge color="red" dot sm>Blocked Access</Badge>
                            ) : provider.is_active ? (
                                <Badge color="green" dot sm>{t("status_active", "Active")}</Badge>
                            ) : (
                                <Badge color="gray" dot sm>{t("status_inactive", "Inactive")}</Badge>
                            )}
                        </div>

                        <div>
                            <div className="text-muted dark:text-slate-400 mb-1">{t("col_verification", "Admin Verification")}</div>
                            <Badge color={provider.verification_status === 'verified' ? 'green' : provider.verification_status === 'rejected' ? 'red' : 'amber'} dot sm>
                                {provider.verification_status ? provider.verification_status.toUpperCase() : 'PENDING'}
                            </Badge>
                        </div>

                        <div>
                            <div className="text-muted dark:text-slate-400 mb-1">{t("label_mobile", "Mobile Number")}</div>
                            <div className="font-mono font-semibold">{provider.mobile_number || '—'}</div>
                        </div>

                        <div>
                            <div className="text-muted dark:text-slate-400 mb-1">Vehicle / ID</div>
                            <div className="font-medium truncate">{provider.vehicle_number || provider.identity_number || '—'}</div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    {(provider.emergency_contact_number || provider.email || provider.address) && (
                        <div className="p-3.5 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-muted dark:text-slate-400 mb-2.5">
                                Contact Information
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {provider.emergency_contact_number && (
                                    <div>
                                        <div className="text-muted dark:text-slate-400 mb-0.5">Emergency Contact</div>
                                        <div className="font-semibold text-red-600 dark:text-red-400">{provider.emergency_contact_number}</div>
                                    </div>
                                )}
                                {provider.email && (
                                    <div>
                                        <div className="text-muted dark:text-slate-400 mb-0.5">Email</div>
                                        <div className="font-medium truncate">{provider.email}</div>
                                    </div>
                                )}
                                {provider.address && (
                                    <div className="sm:col-span-3">
                                        <div className="text-muted dark:text-slate-400 mb-0.5">Address</div>
                                        <div className="font-medium text-slate-700 dark:text-slate-300">{provider.address}</div>
                                    </div>
                                )}
                                <div>
                                    <h3 className="text-2xl font-bold tracking-tight">{provider.name}</h3>
                                    <p className="text-slate-500 dark:text-slate-400 capitalize font-medium">{t("type_" + provider.service_type.toLowerCase().replace(/ /g, "_"))}</p>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded text-xs font-bold tracking-wider">
                                            ID: SP-{String(provider.id).padStart(4, '0')}
                                        </span>
                                        <Badge variant={provider.status === 'active' ? 'success' : (provider.status === 'blocked' ? 'danger' : 'warning')} className="text-xs">
                                            {provider.status === 'active' ? t("status_active", {defaultValue: "Active"}) : (provider.status === 'blocked' ? 'Blocked' : t("status_inactive", {defaultValue: "Inactive"}))}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Assigned Units & Access Schedules */}
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-muted dark:text-slate-400 mb-2.5">
                            Assigned Units & Access Schedules
                        </h4>
                        {provider.units && provider.units.length > 0 ? (
                            <div className="space-y-3">
                                {provider.units.map((unitAssign, idx) => {
                                    const unitLabel = unitAssign.unit
                                        ? (unitAssign.unit.block ? `${unitAssign.unit.block}-${unitAssign.unit.unit_number}` : (unitAssign.unit.flat_number || unitAssign.unit.unit_number))
                                        : 'General Access';
                                    const residentName = unitAssign.member?.name || unitAssign.unit?.users?.[0]?.name || 'Society Resident';

                                    return (
                                        <div key={idx} className="p-3.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm text-xs">
                                            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 border-b dark:border-slate-800 pb-2.5 mb-2.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold rounded border border-indigo-200 dark:border-indigo-800">
                                                        Unit: {unitLabel}
                                                    </span>
                                                    <span className="text-slate-600 dark:text-slate-300">
                                                        Resident: <strong className="text-ink dark:text-white">{residentName}</strong>
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {unitAssign.access_status && (
                                                        <Badge variant={unitAssign.access_status === 'enabled' ? 'success' : (unitAssign.access_status === 'expired' ? 'warning' : 'danger')} className="text-[10px]">
                                                            {unitAssign.access_status === 'enabled' ? 'Enabled' : (unitAssign.access_status === 'expired' ? 'Expired' : 'Disabled')}
                                                        </Badge>
                                                    )}
                                                    <div className="text-muted dark:text-slate-400 text-[11px]">
                                                        <span>Duration: {formatDateByPreference(unitAssign.start_date)}</span>
                                                        {unitAssign.end_date && <span> to {formatDateByPreference(unitAssign.end_date)}</span>}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-2">
                                                <span className="text-muted dark:text-slate-400">Access Mode: </span>
                                                <strong className="capitalize text-ink dark:text-slate-200">
                                                    {unitAssign.access_type === 'scheduled' ? 'Scheduled Time Slots' : (unitAssign.access_type === 'temporary' ? 'Temporary Access' : 'Permanent / Unrestricted (24x7)')}
                                                </strong>
                                            </div>

                                            {unitAssign.access_type === 'scheduled' && unitAssign.schedules && unitAssign.schedules.length > 0 ? (
                                                <div className="space-y-2 mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                                                    {[...new Set(unitAssign.schedules.map(s => s.day_of_week))].sort().map(dayIndex => (
                                                        <div key={dayIndex} className="bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50 rounded-md p-2">
                                                            <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5 border-b border-slate-200/60 dark:border-slate-700/60 pb-1">
                                                                {t("day_" + DAYS[dayIndex].toLowerCase(), {defaultValue: DAYS[dayIndex]})}
                                                            </div>
                                                            <div className="space-y-1">
                                                                {unitAssign.schedules.filter(s => s.day_of_week === dayIndex)
                                                                    .sort((a, b) => (a.entry_start_time || '').localeCompare(b.entry_start_time || ''))
                                                                    .map(schedule => (
                                                                    <div key={schedule.id} className="text-[11px] flex items-center text-slate-600 dark:text-slate-400">
                                                                        <span className="w-1 h-1 rounded-full bg-primary/60 mr-2"></span>
                                                                        <span className="font-mono">{schedule.entry_start_time?.substring(0, 5)} - {schedule.entry_end_time?.substring(0, 5)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : unitAssign.access_type === 'temporary' ? (
                                                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1.5">
                                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                    Temporary Access Granted
                                                </div>
                                            ) : (
                                                <div className="text-slate-500 italic text-[11px]">
                                                    Always allowed entry during active service dates.
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-xl text-amber-800 dark:text-amber-300 text-xs">
                                No active unit assignments. Please edit this provider to assign a unit before activation.
                            </div>
                        )}
                    </div>

                    {/* ID Proof and Verification Document Details */}
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-muted dark:text-slate-400 mb-2">
                            ID Proof & Documents
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                            <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-800">
                                <div className="text-muted dark:text-slate-400">Identity Proof</div>
                                <div className="font-semibold mt-0.5">{provider.identity_type || 'Aadhaar / Government ID'}</div>
                                <div className="font-mono text-[11px] text-slate-600 dark:text-slate-300">{provider.identity_number || 'Not provided'}</div>
                            </div>

                            <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-800">
                                <div className="text-muted dark:text-slate-400">Police Verification</div>
                                <div className="font-semibold mt-0.5">Verified on Record</div>
                                <div className="text-[11px] text-emerald-600 dark:text-emerald-400">Status: Completed</div>
                            </div>

                            <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-800">
                                <div className="text-muted dark:text-slate-400">Record Timestamps</div>
                                <div className="text-[11px] mt-0.5">Created: {formatDateByPreference(provider.created_at)}</div>
                                <div className="text-[11px]">Updated: {formatDateByPreference(provider.updated_at)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Access / Entry History */}
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-muted dark:text-slate-400 mb-2">
                            Recent Gate Entry / Access History
                        </h4>
                        {loadingEntries ? (
                            <div className="p-4 text-center text-xs text-muted">Loading access logs...</div>
                        ) : entries && entries.length > 0 ? (
                            <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-muted">
                                        <tr>
                                            <th className="p-2.5">Entry Time</th>
                                            <th className="p-2.5">Exit Time</th>
                                            <th className="p-2.5">Gate Status</th>
                                            <th className="p-2.5">Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {entries.slice(0, 5).map((log, lIdx) => (
                                            <tr key={lIdx}>
                                                <td className="p-2.5 font-mono">{formatDateByPreference(log.entry_time)}</td>
                                                <td className="p-2.5 font-mono">{log.exit_time ? formatDateByPreference(log.exit_time) : '— (Inside)'}</td>
                                                <td className="p-2.5">
                                                    <Badge color={log.exit_time ? 'gray' : 'green'} sm dot>
                                                        {log.exit_time ? 'Completed' : 'Inside Premise'}
                                                    </Badge>
                                                </td>
                                                <td className="p-2.5 text-slate-500">{log.remarks || 'Normal Entry'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-3.5 text-center text-xs text-muted dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-800">
                                No gate entry records found yet.
                            </div>
                        )}
                    </div>
                </div>
            ) : activeTab === 'documents' ? (
                <div className="max-h-[75vh] overflow-y-auto px-1">
                    <ServiceProviderDocuments providerId={provider.id} />
                </div>
            ) : (
                <div className="max-h-[75vh] overflow-y-auto px-1">
                    <ServiceProviderHistory provider={provider} />
                </div>
            )}
        </Modal>
    );
};

export default ViewVisitorServiceProviderModal;
